<script setup>
import { computed } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  label: {
    type: String,
    default: null,
  },
  trailingIcon: {
    type: String,
    default: null,
  },
  isMainTool: {
    type: Boolean,
    default: true,
  },
});

const displayLabel = computed(() => {
  if (props.label) return props.label;
  if (props.item.label) return props.item.label;
  return props.item.title || props.item.name || 'Unknown Item';
});

const itemType = computed(() => props.item.itemType);

const hasIcon = computed(() => {
  return props.item.icon && props.item.icon.startsWith('i-lucide-');
});

const isAttribute = computed(() =>
  ['attribute', 'contact_attribute', 'event_attribute', 'event_field'].includes(
    itemType.value
  )
);

const typeLabelKey = computed(() => {
  if (itemType.value === 'tool') return 'ASSISTANT_MGMT.TOOLBOX.TOOL_LABEL';
  if (itemType.value === 'library')
    return 'ASSISTANT_MGMT.TOOLBOX.LIBRARY_LABEL';
  if (itemType.value === 'visualization') {
    return 'ASSISTANT_MGMT.TOOLBOX.VISUALIZATION_LABEL';
  }
  if (itemType.value === 'event_field') {
    return 'ASSISTANT_MGMT.TOOLBOX.EVENT_FIELD_LABEL';
  }
  return 'ASSISTANT_MGMT.TOOLBOX.ATTRIBUTE_LABEL';
});

const pillClasses = computed(() => ({
  'bg-purple-100 text-purple-800': itemType.value === 'tool',
  'bg-amber-100 text-amber-800': isAttribute.value,
  'bg-orange-100 text-orange-800': itemType.value === 'event_field',
  'bg-teal-100 text-teal-800': itemType.value === 'visualization',
  'text-[0.8125rem]': !props.isMainTool,
}));

const badgeClasses = computed(() => [
  itemType.value === 'tool'
    ? 'border border-purple-200 text-purple-800 dark:text-purple-200'
    : itemType.value === 'library'
      ? 'border border-indigo-200 text-indigo-800 dark:text-indigo-200'
      : itemType.value === 'visualization'
        ? 'border border-teal-200 text-teal-800 dark:text-teal-200'
        : itemType.value === 'event_field'
          ? 'border border-orange-200 text-orange-800 dark:text-orange-200'
          : 'border border-amber-200 text-amber-800 dark:text-amber-200',
  props.isMainTool ? 'px-2 py-0.5 text-xs' : 'px-1.5 py-0.5 text-[0.6875rem]',
]);

const labelClasses = computed(() => ({
  'text-purple-800': itemType.value === 'tool',
  'text-amber-800': isAttribute.value && itemType.value !== 'event_field',
  'text-orange-800': itemType.value === 'event_field',
  'text-blue-800': itemType.value === 'library',
  'text-teal-800': itemType.value === 'visualization',
}));

const libraryStyle = computed(() =>
  itemType.value === 'library'
    ? { backgroundColor: '#E1E9FF', color: '#3A5BC7' }
    : {}
);
</script>

<template>
  <div
    class="inline-flex items-center gap-2 rounded-xl text-sm py-0.5 pr-2 pl-0.5 hover:shadow-sm transition-shadow max-w-full"
    :class="pillClasses"
    :style="libraryStyle"
  >
    <span
      class="bg-white rounded-xl font-bold flex-shrink-0"
      :class="badgeClasses"
    >
      {{ $t(typeLabelKey) }}
    </span>
    <span
      class="font-medium rounded flex-1 min-w-0 leading-tight text-xs flex truncate items-center gap-1 align-center"
      :class="labelClasses"
    >
      <Icon v-if="hasIcon" :icon="item.icon" class="h-3 w-3 flex-shrink-0" />
      <span class="-mt-[1px]">{{ displayLabel }}</span>
    </span>
    <div
      v-if="trailingIcon"
      class="flex flex-shrink-0 ml-auto transition-opacity"
      :class="labelClasses"
    >
      <i class="w-3 h-3 opacity-50" :class="[trailingIcon]" />
    </div>
    <slot name="trailing" />
  </div>
</template>
